import type { User, UserManager } from 'oidc-client-ts'
import { setAuthTokenToCookies } from 'api/auth'
import { withRenewLock } from './renewLock'

// Renew this far ahead of expiry. Assumes the access-token TTL comfortably exceeds
// it: a TTL at or below this would read as permanently stale and renew every time.
export const OIDC_REFRESH_LEEWAY_SECONDS = 600

let userManagerPromise: Promise<UserManager> | undefined
let renewInFlight: Promise<string | undefined> | undefined
let onTokenSettled: ((accessToken: string) => void) | undefined

// tokenLoader sends Redux's copy of the bearer, and only signinSilent mirrors
// there (via userLoaded). Adopting another tab's token wrote the cookie alone, so
// a tab that never renews itself served a stale bearer until a reload — and a
// mutation in that window 401s into a logout. Every settle point reports instead.
export function setOidcTokenListener(listener: (accessToken: string) => void): void {
  onTokenSettled = listener
}

export function clearOidcTokenListener(listener: (accessToken: string) => void): void {
  if (onTokenSettled === listener) {
    onTokenSettled = undefined
  }
}

function settleToken(accessToken: string): string {
  setAuthTokenToCookies(accessToken)
  onTokenSettled?.(accessToken)

  return accessToken
}

// One long-lived UserManager shared with the coordinator, so silent renew stays
// armed and two managers don't race the rotating refresh token. oidc-client-ts
// stays lazy (./config) for bundle isolation from non-OIDC brands.
export function getOidcUserManager(): Promise<UserManager> {
  if (!userManagerPromise) {
    userManagerPromise = createOidcUserManagerAsync()
  }
  return userManagerPromise
}

async function createOidcUserManagerAsync(): Promise<UserManager> {
  try {
    const { createOidcUserManager } = await import('./config')
    return createOidcUserManager()
  } catch(error) {
    // Don't memoise a failed construction, or 401 recovery stays dead until reload.
    userManagerPromise = undefined
    throw error
  }
}

export function isAccessTokenStale(user: User | null | undefined, nowSeconds: number): boolean {
  if (!user?.access_token || typeof user.expires_at !== 'number') {
    return true
  }
  return nowSeconds >= user.expires_at - OIDC_REFRESH_LEEWAY_SECONDS
}

async function readStoredUser(userManager: UserManager): Promise<User | undefined> {
  try {
    const { readStoredOidcUser } = await import('./config')
    // `return await`, so the catch below sees a rejection.
    return await readStoredOidcUser(userManager)
  } catch {
    return undefined
  }
}

// Lets the renewer tell a dead refresh token from one another tab rotated away;
// svc-auth refuses both identically.
export async function hasLiveStoredOidcSession(): Promise<boolean> {
  const userManager = await getOidcUserManager()
  const stored = await readStoredUser(userManager)

  return Boolean(stored?.access_token) && stored?.expired === false
}

async function renew(tokenAtEntry: string | undefined, force: boolean): Promise<string | undefined> {
  const userManager = await getOidcUserManager()

  return withRenewLock(userManager.settings.client_id, async() => {
    // The winner has already refreshed, so adopt its token rather than spend the
    // one it rotated out. `force` also requires the token to have moved: the one
    // the caller arrived with is what the server just rejected, however fresh.
    const stored = await readStoredUser(userManager)
    if (
      stored?.access_token &&
      !isAccessTokenStale(stored, Date.now() / 1000) &&
      (!force || stored.access_token !== tokenAtEntry)
    ) {
      return settleToken(stored.access_token)
    }

    // signinSilent raises userLoaded itself, so mirrorToken has already written
    // the cookie and dispatched by the time this settles — a known, benign
    // overlap. The reducer no-ops on an unchanged token, and the repeated cookie
    // write still ends on a write, so the expire-then-write order holds.
    const refreshed = await userManager.signinSilent()
    const accessToken = refreshed?.access_token

    return accessToken ? settleToken(accessToken) : accessToken
  })
}

// Single-flight within the tab. The freshness check stays outside it, or a forced
// caller could be handed the very token the server just rejected.
function renewOnce(tokenAtEntry: string | undefined, force: boolean): Promise<string | undefined> {
  if (!renewInFlight) {
    renewInFlight = renew(tokenAtEntry, force).finally(() => {
      renewInFlight = undefined
    })
  }
  return renewInFlight
}

// force: the caller knows the current token is unacceptable (a 401), so skip the
// freshness check and always renew.
export async function ensureValidOidcSession(options?: { force?: boolean }): Promise<string | undefined> {
  const force = options?.force ?? false
  const userManager = await getOidcUserManager()
  const current = await readStoredUser(userManager)

  if (!force && current?.access_token && !isAccessTokenStale(current, Date.now() / 1000)) {
    return settleToken(current.access_token)
  }

  return renewOnce(current?.access_token, force)
}
